import pluralize from "pluralize";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export const useStringUtils = () => {
  const toPlural = (word?: string | null): string => {
    if (!word) return "";
    return pluralize(word);
  };

  const toSingular = (word?: string | null): string => {
    if (!word) return "";
    return pluralize.singular(word);
  };

  /**
   * Returns a label that includes the count and correctly pluralized word.
   * Example: pluralizeWithCount("Episode", 1) -> "1 Episode"
   *          pluralizeWithCount("Episode", 3) -> "3 Episodes"
   *
   * @param word The word to pluralize (required)
   * @param count The numeric count (optional, defaults to 0 when not a valid number)
   * @param includeNumber Whether to prefix the result with the numeric count (default true)
   */
  const pluralizeWithCount = (
    word?: string | null,
    count?: number | null,
    includeNumber = true
  ): string => {
    if (!word) return "";
    const n = typeof count === "number" && !isNaN(count) ? count : 0;
    const label = pluralize(word, n);
    return includeNumber ? `${n} ${label}` : label;
  };

  /**
   * Extracts initials from a name (max 2 letters)
   * Example: "John Doe" -> "JD", "Madonna" -> "MA"
   */
  const getInitials = (name?: string | null): string => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0]!.substring(0, 2).toUpperCase();
    }
    return (
      parts
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase()
    );
  };

  /**
   * Capitalizes the first letter of a string
   * Example: "hello" -> "Hello"
   */
  const capitalize = (str?: string | null): string => {
    if (!str) return "";
    const s = String(str).trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  /**
   * Converts a string to Title Case (first letter of every word capitalized)
   * Example: "hello world" -> "Hello World"
   */
  const toSentenceCase = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((word) => capitalize(word))
      .join(" ");
  };

  /**
   * Converts kebab-case or snake_case into human readable format
   * Example: "super-admin" -> "Super Admin"
   */
  const humanize = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .replace(/[-_]+/g, " ")
      .trim()
      .split(/\s+/)
      .map((word) => capitalize(word))
      .join(" ");
  };

  /**
   * Converts a string into a URL-friendly slug
   * Example: "Super Admin" -> "super-admin"
   */
  const slugify = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove non word characters except space & dash
      .replace(/\s+/g, "-") // spaces -> dash
      .replace(/-+/g, "-"); // collapse multiple dashes
  };

  /**
   * Converts a string into camelCase
   * Example: "super admin" -> "superAdmin"
   */
  const camelCase = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .trim()
      .replace(/[-_ ]+(.)/g, (_, chr) => chr.toUpperCase())
      .replace(/^(.)/, (match) => match.toLowerCase());
  };

  /**
   * Converts a string into snake_case
   * Example: "Super Admin" -> "super_admin"
   */
  const snakeCase = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .trim()
      .replace(/\s+/g, "_")
      .replace(/-+/g, "_")
      .toLowerCase();
  };

  /**
   * Converts a string into kebab-case
   * Example: "Super Admin" -> "super-admin"
   */
  const kebabCase = (str?: string | null): string => {
    if (!str) return "";
    return String(str)
      .trim()
      .replace(/\s+/g, "-")
      .replace(/_+/g, "-")
      .toLowerCase();
  };

  /**
   * Truncates string to a given length
   * Example: truncate("Hello World", 5) -> "Hello..."
   */
  const truncate = (str?: string | null, length = 50): string => {
    if (!str) return "";
    const s = String(str);
    return s.length > length ? s.substring(0, length) + "..." : s;
  };

  /**
   * Checks if a string contains numbers
   */
  const containsNumber = (str?: string | null): boolean => {
    if (!str) return false;
    return /\d/.test(String(str));
  };

    /**

   * Validates email address

   */

  const isEmail = (value?: string | null): boolean => {

    if (!value) return false;

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(

      String(value).trim()

    );

  };

  /**

   * Validates mobile number

   * Default country: Ghana (GH)

   */

  const isMobileNumber = (

    value?: string | null,

    defaultCountry: "GH" | "US" | "GB" = "GH"

  ): boolean => {

    if (!value) return false;

    const phone = parsePhoneNumberFromString(

      String(value).trim(),

      defaultCountry

    );

    return phone?.isValid() || false;

  };

  /**

   * Detects whether input is phone, email or unknown

   */

  const detectContactType = (

    value?: string | null

  ): "mobile" | "email" | "unknown" => {

    if (!value) return "unknown";

    if (isMobileNumber(value)) {

      return "mobile";

    }

    if (isEmail(value)) {

      return "email";

    }

    return "unknown";

  };

  return {
    toPlural,
    toSingular,
    pluralizeWithCount,
    getInitials,
    capitalize,
    toSentenceCase,
    humanize,
    slugify,
    camelCase,
    snakeCase,
    kebabCase,
    truncate,
    containsNumber,
    isEmail,
    isMobileNumber,
    detectContactType,
  };
};